import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  Aviso,
  Botao,
  Campo,
  CampoDeSelecao,
  Cartao,
  CelulaDeAcoes,
  EstadoVazio,
  MenuDeAcoes,
  Modal,
  Selo,
  Tabela,
  useDialogo,
  type ColunaTabela,
  type TomSemantico,
} from '../componentes/ui';
import { EditorDeContrato, type EditorDeContratoHandle } from '../componentes/EditorDeContrato';
import { AvisoDeVariaveisDesconhecidas, PainelDeVariaveis } from '../componentes/VariaveisDoModelo';
import { ConfirmarVariaveis } from '../componentes/ConfirmarVariaveis';
import {
  tokensDesconhecidos,
  type UsoComVariaveis,
  type UsoDoModelo,
} from '../servicos/modelosDeContrato';
import { baixarArquivo, requisicaoAutenticada } from '../servicos/http';
import { ErroDaApi } from './ErroDaApi';

interface ModeloResumido {
  readonly id: string;
  readonly nome: string;
  /** O `.docx` de origem, quando importado do Word. */
  readonly arquivoOrigem: string | null;
  readonly servicoId: string | null;
  readonly servicoNome: string | null;
  readonly temConteudo: boolean;
  readonly atualizadoEm: string;
}

interface ModelosDaEmpresa {
  readonly businessId: string;
  readonly nomeDaEmpresa: string;
  readonly modelos: readonly ModeloResumido[];
  /** `uso`: as variáveis que o serviço oferece (D-020); `null` = serviço sem contrato. */
  readonly servicos: readonly {
    readonly id: string;
    readonly nome: string;
    readonly uso: UsoDoModelo | null;
  }[];
  readonly usos: readonly UsoComVariaveis[];
  readonly padrao: string;
  readonly tokens: readonly string[];
}

interface Modelo {
  readonly id: string;
  readonly businessId: string;
  readonly nome: string;
  readonly arquivoOrigem: string | null;
  readonly servicoId: string | null;
  readonly servicoNome: string | null;
  readonly conteudo: string;
  readonly atualizadoEm: string;
}

/**
 * RN-042, RN-043 / item 16 de 15/09 — Os modelos de contrato da empresa.
 *
 * ## Um ou mais por empresa, cada um com o seu serviço
 *
 * Toda empresa cadastrada pode ter modelos: "Minuta padrão", "Contrato PR",
 * o que a operação precisar. Cada modelo diz a que serviço atende — Créditos
 * ICMS, Passivo, Pontual — e pode ser criado antes de decidir isso. No
 * fechamento do contrato, os modelos das partes para o serviço da operação são
 * oferecidos, e quando há mais de um o assessor escolhe. Sem modelo, o PDF sai
 * no esqueleto padrão — que existe para nunca faltar contrato, não para ser o
 * contrato.
 *
 * ## Os tokens ficam à vista
 *
 * O gerador substitui `[TOKEN]` pelo valor e **apaga** o que não conhece
 * (RN-043). Um token digitado errado não dá erro: some do PDF assinado. Por isso
 * a lista de tokens válidos fica ao lado do editor, clicável — e o aviso de que
 * o que não estiver nela desaparece.
 */
export function MinutaDeContrato(props: {
  readonly businessId: string;
  /**
   * Avisa quem hospeda a seção (a aba Serviços) quando um modelo está aberto
   * no editor, para a aba dar o espaço inteiro a ele em vez de deixar o editor
   * embaixo da tabela de serviços.
   */
  readonly aoMudarEdicao?: (editando: boolean) => void;
}): JSX.Element {
  const { businessId, aoMudarEdicao } = props;
  const queryClient = useQueryClient();
  const dialogo = useDialogo();
  // `null` = lista; `{ id }` = editando; `{ id: null, servicoId }` = criando
  // já com o serviço do grupo em que se clicou.
  const [aberto, setAberto] = useState<{ id: string | null; servicoId?: string } | null>(null);
  // `null` = fechado; senão, o serviço pré-escolhido (ou '' para nenhum).
  const [importando, setImportando] = useState<string | null>(null);
  // `null` = ainda não escolheu; o padrão é decidido ao montar os grupos.
  const [servicoEscolhido, setServicoEscolhido] = useState<string | null>(null);

  useEffect(() => {
    aoMudarEdicao?.(aberto !== null);
  }, [aberto, aoMudarEdicao]);

  const modelos = useQuery({
    queryKey: ['modelos-de-contrato', businessId],
    queryFn: () => requisicaoAutenticada<ModelosDaEmpresa>(`/icms/contrato-template/${businessId}`),
  });

  const excluir = useMutation({
    mutationFn: (modeloId: string) =>
      requisicaoAutenticada<void>(`/icms/contrato-template/${businessId}/${modeloId}`, {
        metodo: 'DELETE',
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['modelos-de-contrato', businessId] });
    },
  });

  if (modelos.isError) {
    return <ErroDaApi titulo="Não foi possível carregar os modelos" erro={modelos.error} />;
  }
  if (modelos.data === undefined) {
    return <p className="py-10 text-center text-sm text-conteudo-sutil">Carregando modelos…</p>;
  }

  const dados = modelos.data;

  if (aberto !== null) {
    return (
      <div className="flex flex-col gap-4">
        <EditorDoModelo
          key={aberto.id ?? 'novo'}
          businessId={businessId}
          modeloId={aberto.id}
          servicoInicial={aberto.servicoId ?? ''}
          catalogo={dados}
          aoVoltar={() => setAberto(null)}
        />
      </div>
    );
  }

  // Agrupados pelo serviço a que atendem: é assim que a operação pensa neles
  // ("o modelo do Recorrente", "o do Passivo") e é por serviço que o
  // fechamento os oferece. Os sem serviço ficam num grupo à parte, com aviso,
  // porque não são oferecidos em fechamento nenhum.
  const grupos: readonly {
    readonly servicoId: string | null;
    readonly nome: string;
    readonly modelos: readonly ModeloResumido[];
  }[] = [
    ...dados.servicos.map((servico) => ({
      servicoId: servico.id,
      nome: servico.nome,
      modelos: dados.modelos.filter((m) => m.servicoId === servico.id),
    })),
    ...(() => {
      const orfaos = dados.modelos.filter(
        (m) => m.servicoId === null || !dados.servicos.some((s) => s.id === m.servicoId),
      );
      return orfaos.length === 0
        ? []
        : [{ servicoId: null, nome: 'Sem serviço definido', modelos: orfaos }];
    })(),
  ];

  // O escolhido, se ainda existir; senão o primeiro serviço que já tem modelo —
  // abrir num serviço vazio quando outro tem modelos esconderia o que existe.
  const grupo =
    grupos.find((g) => chaveDoGrupo(g) === servicoEscolhido) ??
    grupos.find((g) => g.modelos.length > 0) ??
    grupos[0];

  const colunas: readonly ColunaTabela<ModeloResumido>[] = [
    {
      chave: 'nome',
      titulo: 'Modelo',
      larguraMinima: '16rem',
      celula: (m) => (
        <span className="flex flex-col">
          <button
            type="button"
            onClick={() => setAberto({ id: m.id })}
            className="text-left text-sm text-conteudo-primario underline-offset-4 hover:text-acento-ciano hover:underline"
          >
            {m.nome}
          </button>
          {m.arquivoOrigem !== null && (
            <span className="text-xs text-conteudo-sutil">Importado de {m.arquivoOrigem}</span>
          )}
        </span>
      ),
    },
    {
      chave: 'conteudo',
      titulo: 'Texto',
      larguraMinima: '8rem',
      celula: (m) =>
        m.temConteudo ? (
          <Selo tom="positivo" tamanho="pequeno">
            Com texto
          </Selo>
        ) : (
          <Selo tom="neutro" tamanho="pequeno">
            Vazio
          </Selo>
        ),
    },
    {
      chave: 'atualizadoEm',
      titulo: 'Alterado em',
      larguraMinima: '8rem',
      celula: (m) => (
        <span className="valor-fiscal text-sm text-conteudo-secundario">
          {new Date(m.atualizadoEm).toLocaleDateString('pt-BR')}
        </span>
      ),
    },
    {
      chave: 'acoes',
      titulo: 'Ações',
      tituloOculto: true,
      larguraMinima: '3rem',
      celula: (m) => (
        <CelulaDeAcoes>
          <MenuDeAcoes
            rotulo={m.nome}
            itens={[
              { rotulo: 'Editar', aoEscolher: () => setAberto({ id: m.id }) },
              {
                rotulo: 'Excluir',
                perigo: true,
                descricao: 'Os contratos já gerados não mudam.',
                aoEscolher: () => {
                  void (async () => {
                    const ok = await dialogo.confirmar({
                      titulo: `Excluir o modelo "${m.nome}"?`,
                      descricao:
                        'Ele deixa de ser oferecido nos fechamentos. Os PDFs já gerados com ele continuam como estão.',
                      rotuloConfirmar: 'Excluir',
                      tom: 'perigo',
                    });
                    if (ok) excluir.mutate(m.id);
                  })();
                },
              },
            ]}
          />
        </CelulaDeAcoes>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-6">
      {/*
        Um cartão só, com um serviço por linha: o título da seção, o texto de
        cada serviço e a tabela de modelos nascem todos na mesma margem, e os
        botões numa coluna só à direita. Antes eram cartões soltos sob um
        cabeçalho solto, e cada bloco começava num recuo diferente.
      */}
      <Cartao
        como="section"
        titulo="Modelos de contrato"
        subtitulo="Os textos que viram PDF quando um contrato desta empresa é fechado, agrupados pelo serviço a que atendem. Quando as duas partes de uma negociação têm modelo para o serviço, o sistema pede para escolher qual usar no fechamento."
        corpoSemPreenchimento
      >
        <ModalDeImportacao
          businessId={businessId}
          catalogo={dados}
          aberto={importando !== null}
          servicoInicial={importando ?? ''}
          aoFechar={() => setImportando(null)}
          aoImportar={(modelo) => {
            setImportando(null);
            void queryClient.invalidateQueries({ queryKey: ['modelos-de-contrato', businessId] });
            setAberto({ id: modelo.id });
          }}
        />

        {(dados.servicos.length === 0 || excluir.isError) && (
          <div className="flex flex-col gap-3 px-6 pt-4">
            {dados.servicos.length === 0 && (
              <Aviso tom="atencao" titulo="Nenhum serviço no catálogo">
                Os modelos são atribuídos a um serviço. Cadastre os serviços do catálogo — Créditos
                ICMS Recorrente, Passivos, Pontual — para que os modelos possam ser oferecidos no
                fechamento.
              </Aviso>
            )}
            {excluir.isError && (
              <ErroDaApi titulo="Não foi possível excluir" erro={excluir.error} />
            )}
          </div>
        )}

        {grupo === undefined ? (
          <div className="px-6 pb-6 pt-4">
            <EstadoVazio
              titulo="Nenhum modelo cadastrado"
              descricao="Sem modelo, os contratos desta empresa saem no esqueleto padrão. Crie o primeiro e escolha a que serviço ele atende."
              acao={<Botao onClick={() => setAberto({ id: null })}>Criar modelo</Botao>}
            />
          </div>
        ) : (
          /*
            Um serviço por vez, escolhido no seletor. Com os três abertos, a
            seção era três faixas quase sempre vazias repetindo os mesmos dois
            botões. A contagem vai no rótulo de cada opção para o seletor não
            esconder onde já existe modelo.
          */
          <div className="mt-4 flex flex-col gap-4 border-t border-borda px-6 py-4">
            <div className="flex flex-wrap items-end justify-between gap-4">
              <CampoDeSelecao
                rotulo="Serviço"
                className="w-full sm:w-96"
                value={chaveDoGrupo(grupo)}
                onChange={(e) => setServicoEscolhido(e.target.value)}
                opcoes={grupos.map((g) => ({
                  valor: chaveDoGrupo(g),
                  rotulo: `${g.nome} — ${contagemDeModelos(g.modelos.length)}`,
                }))}
              />
              {grupo.servicoId !== null && (
                <div className="flex shrink-0 flex-wrap gap-2">
                  <Botao
                    variante="secundario"
                    tamanho="pequeno"
                    onClick={() => setImportando(grupo.servicoId ?? '')}
                  >
                    Importar do Word
                  </Botao>
                  <Botao
                    tamanho="pequeno"
                    onClick={() => setAberto({ id: null, servicoId: grupo.servicoId ?? '' })}
                  >
                    Novo modelo
                  </Botao>
                </div>
              )}
            </div>

            <p className="text-sm text-conteudo-secundario">
              {grupo.servicoId === null
                ? 'Modelos sem serviço não são oferecidos em fechamento algum. Abra cada um e escolha o serviço.'
                : grupo.modelos.length === 0
                  ? 'Nenhum modelo para este serviço: os contratos dele saem no esqueleto padrão.'
                  : `${grupo.modelos.length} ${grupo.modelos.length === 1 ? 'modelo oferecido' : 'modelos oferecidos'} no fechamento.`}
            </p>

            {grupo.modelos.length > 0 && (
              <Tabela
                legenda={`Modelos de contrato — ${grupo.nome}`}
                densidade="compacta"
                colunas={colunas}
                dados={grupo.modelos}
                chaveDaLinha={(m) => m.id}
                chaveDeCabecalhoDaLinha="nome"
              />
            )}
          </div>
        )}
      </Cartao>

      <DocumentosGerados businessId={businessId} />
    </div>
  );
}

/** Valor do seletor: o id do serviço, ou uma chave fixa para os sem serviço. */
function chaveDoGrupo(grupo: { readonly servicoId: string | null }): string {
  return grupo.servicoId ?? 'sem-servico';
}

function contagemDeModelos(n: number): string {
  if (n === 0) return 'nenhum modelo';
  return n === 1 ? '1 modelo' : `${n} modelos`;
}

/* ─── Editor ──────────────────────────────────────────────────────────────── */

function EditorDoModelo(props: {
  readonly businessId: string;
  /** `null` cria. */
  readonly modeloId: string | null;
  /** Ao criar, o serviço já escolhido (o do grupo em que se clicou). */
  readonly servicoInicial: string;
  readonly catalogo: ModelosDaEmpresa;
  readonly aoVoltar: () => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const { businessId, modeloId, catalogo } = props;
  const [verHtml, setVerHtml] = useState(false);
  const editorRef = useRef<EditorDeContratoHandle>(null);
  /** D-022 — as variáveis novas, confirmadas antes de salvar ou pelo atalho do aviso. */
  const [confirmando, setConfirmando] = useState<'salvar' | 'so-criar' | null>(null);
  const [form, setForm] = useState<{ nome: string; servicoId: string; conteudo: string } | null>(
    modeloId === null ? { nome: '', servicoId: props.servicoInicial, conteudo: '' } : null,
  );

  const modelo = useQuery({
    queryKey: ['modelo-de-contrato', businessId, modeloId],
    queryFn: () =>
      requisicaoAutenticada<Modelo>(`/icms/contrato-template/${businessId}/${modeloId ?? ''}`),
    enabled: modeloId !== null,
  });

  // O rascunho nasce do que está gravado, e só quando a resposta chega. Iniciar
  // com string vazia faria o editor piscar em branco e, se alguém salvasse
  // nesse instante, apagaria o modelo.
  useEffect(() => {
    if (modelo.data !== undefined && form === null) {
      setForm({
        nome: modelo.data.nome,
        servicoId: modelo.data.servicoId ?? '',
        conteudo: modelo.data.conteudo,
      });
    }
  }, [modelo.data, form]);

  const salvar = useMutation({
    mutationFn: (dados: { nome: string; servicoId: string; conteudo: string }) => {
      const corpo = {
        nome: dados.nome,
        servicoId: dados.servicoId === '' ? null : dados.servicoId,
        conteudo: dados.conteudo,
      };
      return modeloId === null
        ? requisicaoAutenticada<Modelo>(`/icms/contrato-template/${businessId}`, {
            metodo: 'POST',
            corpo,
          })
        : requisicaoAutenticada<Modelo>(`/icms/contrato-template/${businessId}/${modeloId}`, {
            metodo: 'PUT',
            corpo,
          });
    },
    onSuccess: (gravado) => {
      queryClient.setQueryData(['modelo-de-contrato', businessId, gravado.id], gravado);
      void queryClient.invalidateQueries({ queryKey: ['modelos-de-contrato', businessId] });
      // Criar leva de volta à lista; editar fica, com o rascunho alinhado.
      if (modeloId === null) props.aoVoltar();
      else
        setForm({
          nome: gravado.nome,
          servicoId: gravado.servicoId ?? '',
          conteudo: gravado.conteudo,
        });
    },
  });

  if (modelo.isError) {
    return <ErroDaApi titulo="Não foi possível carregar o modelo" erro={modelo.error} />;
  }
  if (form === null) {
    return <p className="py-10 text-center text-sm text-conteudo-sutil">Carregando modelo…</p>;
  }

  const gravado = modelo.data;
  const alterado =
    gravado === undefined
      ? form.nome !== '' || form.conteudo !== ''
      : form.nome !== gravado.nome ||
        form.servicoId !== (gravado.servicoId ?? '') ||
        form.conteudo !== gravado.conteudo;
  const podeSalvar = form.nome.trim() !== '' && alterado;
  // D-020 — as variáveis do serviço do modelo: as do Pontual num modelo do
  // Pontual. Sem serviço (ou um que não gera contrato), a lista antiga.
  const usoDoServico = catalogo.servicos.find((s) => s.id === form.servicoId)?.uso ?? null;
  const uso = catalogo.usos.find((u) => u.id === usoDoServico) ?? null;
  const inserir = (token: string): void => {
    if (verHtml) setForm((f) => (f === null ? f : { ...f, conteudo: `${f.conteudo}[${token}]` }));
    else editorRef.current?.inserir(`[${token}]`);
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <button
            type="button"
            onClick={props.aoVoltar}
            className="text-sm text-conteudo-secundario underline-offset-4 hover:text-conteudo-primario hover:underline"
          >
            ← Modelos de contrato
          </button>
          <h2 className="mt-2 font-display text-lg font-semibold tracking-tight">
            {modeloId === null ? 'Novo modelo' : gravado?.nome}
          </h2>
          {gravado !== undefined && (
            <p className="mt-1 text-sm text-conteudo-secundario">
              {gravado.arquivoOrigem !== null && <>Importado de {gravado.arquivoOrigem}. </>}
              Alterado pela última vez em{' '}
              {new Date(gravado.atualizadoEm).toLocaleDateString('pt-BR')}.
            </p>
          )}
        </div>
        <div className="flex flex-wrap gap-2">
          {form.conteudo.trim() === '' && (
            <Botao
              variante="secundario"
              onClick={() =>
                setForm((f) =>
                  f === null ? f : { ...f, conteudo: uso?.esqueleto ?? catalogo.padrao },
                )
              }
            >
              Partir do esqueleto padrão
            </Botao>
          )}
          <Botao
            onClick={() =>
              uso !== null && tokensDesconhecidos(form.conteudo, uso).length > 0
                ? setConfirmando('salvar')
                : salvar.mutate(form)
            }
            disabled={!podeSalvar}
            carregando={salvar.isPending}
          >
            {modeloId === null ? 'Criar modelo' : 'Salvar modelo'}
          </Botao>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2">
        <Campo
          rotulo="Nome do modelo"
          required
          placeholder="Minuta padrão, Contrato PR…"
          value={form.nome}
          onChange={(e) => setForm((f) => (f === null ? f : { ...f, nome: e.target.value }))}
        />
        <CampoDeSelecao
          rotulo="Serviço a que atende"
          ajuda="Só modelos com serviço são oferecidos no fechamento. Pode definir depois."
          value={form.servicoId}
          onChange={(e) => setForm((f) => (f === null ? f : { ...f, servicoId: e.target.value }))}
          opcoes={[
            { valor: '', rotulo: 'Ainda não definido' },
            ...catalogo.servicos.map((s) => ({ valor: s.id, rotulo: s.nome })),
          ]}
        />
      </div>

      {salvar.isSuccess && !alterado && (
        <Aviso tom="positivo" titulo="Modelo salvo">
          Os próximos contratos que escolherem este modelo usam este texto. Os já gerados não mudam
          — o PDF assinado é imutável.
        </Aviso>
      )}
      {salvar.isError && <ErroDaApi titulo="Não foi possível salvar" erro={salvar.error} />}
      {uso !== null && (
        <AvisoDeVariaveisDesconhecidas
          conteudo={form.conteudo}
          uso={uso}
          aoCriar={() => setConfirmando('so-criar')}
        />
      )}
      {uso !== null && confirmando !== null && (
        <ConfirmarVariaveis
          onde={{ contrato: uso.id }}
          tokens={tokensDesconhecidos(form.conteudo, uso)}
          rotuloConfirmar={confirmando === 'salvar' ? 'Confirmar e salvar' : 'Criar variáveis'}
          aoCancelar={() => setConfirmando(null)}
          aoConcluir={() => {
            void queryClient.invalidateQueries({ queryKey: ['modelos-de-contrato', businessId] });
            if (confirmando === 'salvar') salvar.mutate(form);
            setConfirmando(null);
          }}
        />
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_20rem]">
        <div className="flex min-w-0 flex-col gap-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-sm font-medium text-conteudo-primario">
              Texto do contrato
              {alterado && <span className="ml-2 text-xs text-atencao">alterações não salvas</span>}
            </span>
            {/* O HTML continua acessível para quem precisa dele — é o que o PDF lê. */}
            <button
              type="button"
              onClick={() => setVerHtml((v) => !v)}
              className="text-xs text-conteudo-secundario underline-offset-4 hover:text-conteudo-primario hover:underline"
            >
              {verHtml ? 'Voltar ao editor' : 'Ver HTML'}
            </button>
          </div>

          {verHtml ? (
            <textarea
              value={form.conteudo}
              onChange={(e) =>
                setForm((f) => (f === null ? f : { ...f, conteudo: e.target.value }))
              }
              spellCheck={false}
              rows={26}
              className="valor-fiscal w-full rounded-card border border-borda bg-superficie-base p-3 text-xs leading-relaxed text-conteudo-primario focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-acento-ciano"
            />
          ) : (
            <EditorDeContrato
              ref={editorRef}
              value={form.conteudo}
              onChange={(html) => setForm((f) => (f === null ? f : { ...f, conteudo: html }))}
              {...(uso === null
                ? { tokens: catalogo.tokens }
                : {
                    variaveis: uso.grupos.flatMap((g) =>
                      g.variaveis.map((v) => ({ texto: `[${v.token}]`, rotulo: v.descricao })),
                    ),
                  })}
            />
          )}
          <span className="text-xs text-conteudo-sutil">
            Escreva como no Word. Um modelo com o texto vazio não é oferecido no fechamento — o
            contrato sai no esqueleto padrão.
          </span>
        </div>

        {uso !== null ? (
          <PainelDeVariaveis uso={uso} aoInserir={inserir} />
        ) : (
          /* Acompanha a rolagem: no fim de um contrato longo, as variáveis
             continuam ao lado (abaixo da barra do topo, que tem 4rem). */
          <Cartao titulo="Variáveis" className="h-fit self-start lg:sticky lg:top-20">
            <p className="mb-3 text-xs text-conteudo-secundario">
              Clique para inserir no ponto do cursor. O sistema troca cada uma pelo dado do contrato
              ao gerar o PDF; o que não estiver nesta lista é <strong>apagado</strong>, sem aviso.
            </p>
            <div className="flex max-h-[28rem] flex-wrap gap-1 overflow-y-auto lg:max-h-[calc(100vh-15rem)]">
              {catalogo.tokens.map((t) => (
                <button
                  key={t}
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => inserir(t)}
                  className="valor-fiscal rounded border border-borda px-1.5 py-0.5 text-[0.6875rem] text-conteudo-secundario transition-colors hover:border-acento-ciano hover:text-acento-ciano focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-acento-ciano"
                >
                  [{t}]
                </button>
              ))}
            </div>
          </Cartao>
        )}
      </div>
    </div>
  );
}

/* ─── Importar do Word ────────────────────────────────────────────────────── */

/**
 * Lista de 17/09 — o modelo pode ser escrito no Word e importado.
 *
 * O `.docx` vira HTML no servidor e entra como qualquer modelo — a tela abre
 * o editor em seguida, para conferir o texto e os `[TOKENS]`. Só `.docx`: o
 * `.doc` antigo não é lido, e o aviso diz isso antes do envio.
 */
function ModalDeImportacao(props: {
  readonly businessId: string;
  readonly catalogo: ModelosDaEmpresa;
  readonly aberto: boolean;
  /** O serviço pré-escolhido ('' para nenhum) — o do grupo em que se clicou. */
  readonly servicoInicial: string;
  readonly aoFechar: () => void;
  readonly aoImportar: (modelo: Modelo) => void;
}): JSX.Element {
  const [nome, setNome] = useState('');
  const [servicoId, setServicoId] = useState(props.servicoInicial);
  const [arquivo, setArquivo] = useState<File | null>(null);

  // Cada abertura vem com o serviço do grupo clicado.
  useEffect(() => {
    if (props.aberto) setServicoId(props.servicoInicial);
  }, [props.aberto, props.servicoInicial]);

  const importar = useMutation({
    mutationFn: async () => {
      if (arquivo === null) throw new Error('Escolha o arquivo .docx.');
      return requisicaoAutenticada<Modelo>(
        `/icms/contrato-template/${props.businessId}/importar-word`,
        {
          metodo: 'POST',
          corpo: {
            nome: nome.trim() === '' ? arquivo.name.replace(/\.docx$/i, '') : nome.trim(),
            servicoId: servicoId === '' ? null : servicoId,
            arquivo: await lerComoBase64(arquivo),
            nomeDoArquivo: arquivo.name,
          },
        },
      );
    },
    onSuccess: (modelo) => {
      setNome('');
      setServicoId('');
      setArquivo(null);
      props.aoImportar(modelo);
    },
  });

  const fechar = (): void => {
    importar.reset();
    props.aoFechar();
  };
  const docx = arquivo !== null && /\.docx$/i.test(arquivo.name);

  return (
    <Modal
      aberto={props.aberto}
      aoFechar={fechar}
      titulo="Importar modelo do Word"
      descricao="O texto do .docx vira o modelo; os [TOKENS] digitados no Word continuam valendo."
      rodape={
        <>
          <Botao variante="secundario" onClick={fechar}>
            Cancelar
          </Botao>
          <Botao onClick={() => importar.mutate()} disabled={!docx} carregando={importar.isPending}>
            Importar
          </Botao>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        <label className="flex flex-col gap-2">
          <span className="text-sm font-medium text-conteudo-secundario">Arquivo Word (.docx)</span>
          <input
            type="file"
            accept=".docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            onChange={(e) => setArquivo(e.target.files?.[0] ?? null)}
            className="text-sm text-conteudo-secundario file:mr-3 file:rounded-card file:border file:border-borda file:bg-superficie-base file:px-3 file:py-2 file:text-sm file:text-conteudo-primario"
          />
          {arquivo !== null && !docx && (
            <span className="text-xs text-erro">
              Só o formato .docx é lido — salve o documento como .docx no Word.
            </span>
          )}
        </label>
        <Campo
          rotulo="Nome do modelo"
          placeholder={
            arquivo === null ? 'Ex.: Minuta padrão' : arquivo.name.replace(/\.docx$/i, '')
          }
          ajuda="Em branco, usa o nome do arquivo."
          value={nome}
          onChange={(e) => setNome(e.target.value)}
        />
        <CampoDeSelecao
          rotulo="Serviço a que atende"
          value={servicoId}
          onChange={(e) => setServicoId(e.target.value)}
          opcoes={[
            { valor: '', rotulo: 'Definir depois' },
            ...props.catalogo.servicos.map((s) => ({ valor: s.id, rotulo: s.nome })),
          ]}
        />
        <Aviso tom="informacao">
          Títulos, parágrafos, negrito, listas e tabelas são preservados. Fonte, margens e cabeçalho
          de página não — o contrato em PDF usa a diagramação do sistema. Depois de importar, o
          texto abre no editor para conferência.
        </Aviso>
        {importar.isError && <ErroDaApi titulo="Não foi possível importar" erro={importar.error} />}
      </div>
    </Modal>
  );
}

/** `FileReader` devolve `data:...;base64,XXX` — só o depois da vírgula interessa. */
async function lerComoBase64(arquivo: File): Promise<string> {
  const leitor = new FileReader();
  return new Promise((resolver, rejeitar) => {
    leitor.onload = () => {
      const resultado = String(leitor.result);
      resolver(resultado.slice(resultado.indexOf(',') + 1));
    };
    leitor.onerror = () => rejeitar(new Error('Não foi possível ler o arquivo.'));
    leitor.readAsDataURL(arquivo);
  });
}

/* ─── Documentos gerados ──────────────────────────────────────────────────── */

interface DocumentoDeContrato {
  readonly id: string;
  readonly clienteId: string;
  readonly clienteNome: string;
  readonly fornecedorId: string;
  readonly fornecedorNome: string;
  /** O assessor do contrato que gerou o PDF — `null` quando não houve. */
  readonly parceiroId: string | null;
  readonly ano: number;
  readonly fileName: string;
  readonly status: string;
  readonly assinadoEm: string | null;
  readonly tamanhoBytes: number;
}

/**
 * RN-045 — Os PDFs que esta minuta gerou, e o que voltou assinado.
 *
 * ## Por que fica junto da minuta
 *
 * Havia uma tela global "Contratos assinados" com todos os PDFs de todos os
 * fornecedores. A pergunta real de quem abre a lista é "os contratos **desta**
 * empresa saíram com o texto certo?" — e a resposta está a uma rolagem do
 * editor que define o texto. A lista global sumiu do menu; a informação mora
 * aqui, filtrada pela empresa.
 *
 * ## Fornecedora OU assessora
 *
 * A minuta é do fornecedor e do assessor (RN-042), e a seção serve os dois:
 * entram os PDFs em que a empresa é a fornecedora **ou** a assessora do
 * contrato. Filtrando só por `fornecedorId`, a aba de uma empresa que só
 * assessora dizia "Nenhum contrato gerado" com os PDFs dela existindo.
 *
 * ## O filtro é no navegador — e a lista inteira é pedida uma vez
 *
 * `GET /icms/contratos-documentos` aceita `ano`, mas a tela **não** o usa: a
 * resposta é uma lista de nomes (o PDF não vem, só o tamanho), pequena o
 * bastante para vir inteira. E as opções do seletor de exercício precisam da
 * lista sem filtro — quando o seletor era montado a partir da resposta
 * filtrada, escolher 2026 fazia 2025 sumir das opções, e só voltava trocando
 * para "Todos" primeiro.
 *
 * `GERADO` é o PDF pronto esperando assinatura; `ASSINADO` já voltou do D4Sign.
 * A diferença importa na cobrança: gerado e não assinado é trabalho que ainda
 * não virou operação.
 */
function DocumentosGerados(props: { readonly businessId: string }): JSX.Element {
  const [ano, setAno] = useState('');

  const documentos = useQuery({
    queryKey: ['contratos-documentos'],
    queryFn: () => requisicaoAutenticada<DocumentoDeContrato[]>('/icms/contratos-documentos'),
  });

  // Todos os documentos desta empresa, em qualquer exercício: é daqui que
  // saem as opções do seletor.
  const daEmpresa = useMemo(
    () =>
      (documentos.data ?? []).filter(
        (d) => d.fornecedorId === props.businessId || d.parceiroId === props.businessId,
      ),
    [documentos.data, props.businessId],
  );

  const anos = useMemo(
    () => [...new Set(daEmpresa.map((d) => d.ano))].sort((a, b) => b - a),
    [daEmpresa],
  );

  // O que a tabela mostra: o recorte do exercício escolhido.
  const doExercicio = useMemo(
    () => (ano === '' ? daEmpresa : daEmpresa.filter((d) => String(d.ano) === ano)),
    [daEmpresa, ano],
  );

  const colunas: readonly ColunaTabela<DocumentoDeContrato>[] = [
    {
      chave: 'status',
      titulo: 'Status',
      larguraMinima: '8rem',
      celula: (d) => <Selo tom={tomDoDocumento(d.status)}>{humanizar(d.status)}</Selo>,
    },
    {
      chave: 'ano',
      titulo: 'Exercício',
      numerica: true,
      larguraMinima: '6rem',
      celula: (d) => <span className="valor-fiscal text-sm">{d.ano}</span>,
    },
    {
      chave: 'clienteNome',
      titulo: 'Cliente',
      larguraMinima: '18rem',
      celula: (d) => (
        <Link
          to={`/clientes/${d.clienteId}`}
          className="text-sm text-conteudo-primario underline-offset-4 hover:text-acento-ciano hover:underline"
        >
          {d.clienteNome}
        </Link>
      ),
    },
    {
      chave: 'papel',
      titulo: 'Papel',
      larguraMinima: '8rem',
      // Uma empresa pode ser fornecedora num contrato e assessora noutro; a
      // coluna diz em qual condição este PDF é dela.
      celula: (d) => (
        <Selo tom="neutro" tamanho="pequeno">
          {d.fornecedorId === props.businessId ? 'Fornecedora' : 'Assessora'}
        </Selo>
      ),
    },
    {
      chave: 'assinadoEm',
      titulo: 'Assinado em',
      larguraMinima: '9rem',
      celula: (d) =>
        d.assinadoEm === null ? (
          <span className="text-xs text-conteudo-sutil">Aguardando</span>
        ) : (
          <span className="valor-fiscal whitespace-nowrap text-sm">
            {new Date(d.assinadoEm).toLocaleDateString('pt-BR')}
          </span>
        ),
    },
    {
      chave: 'arquivo',
      titulo: 'Arquivo',
      alinhamento: 'direita',
      larguraMinima: '11rem',
      celula: (d) => (
        <span className="flex items-center justify-end gap-3">
          <span className="valor-fiscal text-xs text-conteudo-sutil">
            {formatarTamanho(d.tamanhoBytes)}
          </span>
          {/* O PDF mora na linha (`bytea`) e só sai pela rota de download. */}
          <Botao
            variante="sutil"
            tamanho="pequeno"
            onClick={() => {
              void baixarArquivo(`/icms/contratos-documentos/${d.id}/download`, d.fileName);
            }}
          >
            Baixar
          </Botao>
        </span>
      ),
    },
  ];

  // Cartão, e não cabeçalho solto: assim o bloco começa na mesma margem do de
  // modelos, logo acima — texto, tabela e seletor numa coluna só.
  return (
    <Cartao
      como="section"
      titulo="Documentos gerados"
      subtitulo="Os PDFs de contrato em que esta empresa é a fornecedora ou a assessora. Gerado é o contrato esperando assinatura; assinado já voltou do D4Sign. Alterar a minuta não muda o que já foi gerado."
      acao={
        anos.length === 0 ? undefined : (
          <CampoDeSelecao
            rotulo="Exercício"
            className="w-32"
            value={ano}
            onChange={(e) => setAno(e.target.value)}
            opcoes={[
              { valor: '', rotulo: 'Todos' },
              ...anos.map((a) => ({ valor: String(a), rotulo: String(a) })),
            ]}
          />
        )
      }
    >
      {documentos.isError ? (
        <ErroDaApi titulo="Não foi possível carregar os documentos" erro={documentos.error} />
      ) : !documentos.isPending && doExercicio.length === 0 ? (
        <EstadoVazio
          titulo={ano === '' ? 'Nenhum contrato gerado' : `Nenhum contrato em ${ano}`}
          descricao="Os PDFs nascem no fechamento do contrato, na tela do cliente."
        />
      ) : (
        <Tabela
          legenda="Documentos de contrato gerados para esta empresa"
          colunas={colunas}
          dados={doExercicio}
          chaveDaLinha={(d) => d.id}
          chaveDeCabecalhoDaLinha="clienteNome"
          carregando={documentos.isPending}
        />
      )}
    </Cartao>
  );
}

function tomDoDocumento(status: string): TomSemantico {
  if (status === 'ASSINADO') return 'positivo';
  if (status === 'CANCELADO') return 'critico';
  return 'progresso';
}

function humanizar(chave: string): string {
  const t = chave.replace(/_/g, ' ').toLowerCase();
  return t.charAt(0).toUpperCase() + t.slice(1);
}

/** Tamanho em KB ou MB — bytes crus não dizem nada a quem vai baixar. */
function formatarTamanho(bytes: number): string {
  if (bytes === 0) return '—';
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1).replace('.', ',')} MB`;
}
